/**
 * Fraud scoring API client.
 */

import { BaseClient } from '../core/client';
import type { RequestOptions } from '../core/config';
import { ValidationError } from '../core/errors';
import type {
  FraudScoreRequest,
  FraudScoreResponseData,
  FraudScoreResponseEnvelope,
} from './types';

export class FraudClient extends BaseClient {
  /**
   * Submit a single fraud event for scoring.
   */
  async scoreEvent(
    request: FraudScoreRequest,
    requestOptions?: RequestOptions
  ): Promise<FraudScoreResponseEnvelope<FraudScoreResponseData>> {
    this.validateScoreRequest(request);

    return this.requestWithRetry<FraudScoreResponseEnvelope<FraudScoreResponseData>>(
      '/api/v1/fraud/score',
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
      undefined,
      undefined,
      requestOptions
    );
  }

  /**
   * Submit multiple fraud events for scoring.
   */
  async scoreEventsBulk(
    requests: FraudScoreRequest[],
    requestOptions?: RequestOptions
  ): Promise<FraudScoreResponseEnvelope<FraudScoreResponseData[]>> {
    if (!Array.isArray(requests) || requests.length === 0) {
      throw new ValidationError(
        'Invalid bulk score request: at least one score request is required',
        ['requests']
      );
    }

    requests.forEach((request, index) => this.validateScoreRequest(request, index));

    return this.requestWithRetry<FraudScoreResponseEnvelope<FraudScoreResponseData[]>>(
      '/api/v1/fraud/score/bulk',
      {
        method: 'POST',
        body: JSON.stringify(requests),
      },
      undefined,
      undefined,
      requestOptions
    );
  }

  private validateScoreRequest(request: FraudScoreRequest, index?: number): void {
    const errors: string[] = [];
    const prefix = index === undefined ? '' : `requests[${index}].`;

    if (!request.customer_id?.trim()) {
      errors.push(`${prefix}customer_id is required`);
    }
    if (!request.sift_user_id?.trim()) {
      errors.push(`${prefix}sift_user_id is required`);
    }
    if (!request.event_type?.trim()) {
      errors.push(`${prefix}event_type is required`);
    }

    if (errors.length > 0) {
      throw new ValidationError(`Invalid fraud score request: ${errors.join(', ')}`, errors);
    }
  }
}
